// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { MAXIMUM_DATE, MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

export interface DateRangeShortcut {
  label: string;
  getValue: () => [DateTime, DateTime];
}

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * A collection of date range shortcuts that can be used to populate the
   * input.
   */
  shortcuts?: DateRangeShortcut[];
}

/**
 * A controlled date range component that integrates with react-hook-form.
 */
export function FormDateRangePicker<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  return (
    <DateRangePicker
      format="yyyy-MM-dd"
      calendars={2}
      minDate={MINIMUM_DATE}
      maxDate={MAXIMUM_DATE}
      value={field.value}
      onChange={field.onChange}
      slots={{ field: SingleInputDateRangeField }}
      slotProps={{
        shortcuts: { items: props.shortcuts },
        field: { dateSeparator: 'to' },
        toolbar: { hidden: false },
        textField: {
          size: 'small',
          label: props.label,
          error: Boolean(fieldState.error),
          helperText: fieldState.error?.message,
          autoComplete: 'off',
        },
      }}
    />
  );
}
